import React, { forwardRef, ComponentProps } from 'react'
import { useDebounce } from 'react-use'
import compact from 'lodash/compact'
import AutocompleteBase from 'transfix-ui/components/AutocompleteBase'
import {
  TInputChangeHandler,
  useAutocompleteInputState,
  useAutocompleteLazyQuery,
} from 'transfix-ui/components/AutocompleteBase/hooks'
import {
  IAutocompleteBaseOption,
  TAutocompleteBaseChangeHandler,
} from 'transfix-ui/components/AutocompleteBase/types'
import { useDefaultShipperApplicationQuery } from './graphql/DefaultShipperApplication'
import { ISearchShipperApplicationFragment } from './graphql/SearchShipperApplicationFragment'
import { useShipperApplicationsLazyQuery } from './graphql/ShipperApplications'

const getComputedValue = (
  options: IAutocompleteBaseOption[],
  value: string[] | null
): IAutocompleteBaseOption[] | null => {
  const selectedOptions = options.filter(({ id }) => value?.find(valId => valId === id))
  return selectedOptions || null
}

export interface IAutocompleteShipperProps
  extends Omit<
    ComponentProps<typeof AutocompleteBase>,
    'value' | 'onChange' | 'options' | 'loading'
  > {
  value: string[] | null
  required?: boolean
  onChange: (newVal: string[] | null) => void
  inputError?: boolean
  inputValidationMessage?: string
  dataTest?: string
}

export const AutocompleteShipper = forwardRef<HTMLInputElement, IAutocompleteShipperProps>(
  (
    {
      autoSelect = true,
      clearOnBlur = false,
      dataTest = 'AutocompleteShipper',
      inputError,
      inputIcon = 'Search',
      inputValidationMessage,
      onChange,
      placeholder = 'Shipper',
      renderInputType = 'withIcon',
      required,
      selectOnFocus = true,
      size = 'small',
      value: valueProp,
      ...props
    },
    ref
  ) => {
    const { data } = useDefaultShipperApplicationQuery({
      variables: { shipperIds: valueProp || [] },
      skip: !valueProp?.length,
    })
    const { searchShipperApplications } = data || {}
    const { nodes: shipperApplications } = searchShipperApplications || {}
    const [inputValue, setInputValue] = useAutocompleteInputState('')
    const [refetch, { options = [], loading }] = useAutocompleteLazyQuery(
      useShipperApplicationsLazyQuery,
      {
        variables: { searchString: inputValue },
        fetchPolicy: 'network-only',
      },
      {
        value: valueProp || null,
        getOptionsConfig: data => {
          const { searchShipperApplications } = data || {}
          const { nodes } = searchShipperApplications || {}

          return {
            groupIndex: 1,
            options: compact([...(nodes || []), ...(shipperApplications || [])]),
            mapOption: ({ id, name }: ISearchShipperApplicationFragment) => {
              return {
                id,
                text: name || '',
              }
            },
          }
        },
      }
    )

    /**
     * Refetch when the input value changes
     */
    useDebounce(
      () => {
        if (inputValue) {
          refetch({ variables: { searchString: inputValue } })
        }
      },
      250,
      [inputValue]
    )

    const changeHandler: TAutocompleteBaseChangeHandler = (_, newVal) => {
      if (Array.isArray(newVal)) {
        onChange(newVal.map(val => (typeof val === 'string' ? val : val?.id)) || [])
      } else {
        onChange(null)
      }
    }

    const inputChangeHandler: TInputChangeHandler = (e, newVal) => {
      /**
       * MUI triggers an onChange event with `null` value when
       * trying to press delete/backspace when there's a value
       *
       * Because this is an invalid event, we ignore it
       */
      if (e) {
        setInputValue(e, newVal)
      }
    }

    const value = getComputedValue(options, valueProp)

    return (
      <AutocompleteBase
        {...props}
        data-test={dataTest}
        multiple
        ref={ref}
        value={value}
        onChange={changeHandler}
        inputValue={inputValue}
        onInputChange={inputChangeHandler}
        options={options}
        loading={loading}
        placeholder={placeholder}
        loadingText='Loading...'
        selectOnFocus={selectOnFocus}
        autoSelect={autoSelect}
        clearOnBlur={clearOnBlur}
        renderInputType={renderInputType}
        limitTags={1}
        inputIcon={inputIcon}
        size={size}
        required={required}
        inputError={inputError}
        inputValidationMessage={inputValidationMessage}
        filterOptions={node => node}
      />
    )
  }
)
